/**
 * InvoiceActionsModal
 *
 * Modal unificado para Editar y Eliminar facturas.
 * Maneja todos los casos:
 *   - Eliminar con recibos aplicados → warning con lista de recibos bloqueantes
 *   - Editar con monto > abonado → aviso de saldo pendiente
 *   - Editar con monto < abonado → aviso de saldo a FAVOR
 *   - Editar sin recibos → directo
 */

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { IvaSelector } from "../../components/IvaSelector";
import { calcularIva } from "../../shared/iva";
import { overlayClose } from "../../../../shared/utils/overlayClose";
import RubrosModal from "../../../../shared/components/RubrosModal";
import { useEsCliente } from "../../../../shared/hooks/useTenantTipo";
import { SearchSelect } from "../../../../shared/components/SearchSelect";

/** Extrae el id de cuenta, venga como objeto anidado (lectura) o como id plano. */
function cuentaIdDe(r) {
  return typeof r.cuenta_ingreso === "object" && r.cuenta_ingreso !== null
    ? r.cuenta_ingreso.id
    : r.cuenta_ingreso;
}

const OVERLAY = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  zIndex: 1000,
  backdropFilter: "blur(3px)",
};

const MODAL_BASE = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  background: "var(--bg-card)",
  border: "1px solid var(--border-card)",
  borderRadius: 12,
  padding: "24px 24px 20px",
  width: "min(520px, 94vw)",
  zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
};

const LABEL_STYLE = {
  display: "block",
  color: "var(--text-muted)",
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: 4,
};

const INPUT_STYLE = {
  width: "100%",
  padding: "8px 10px",
  background: "var(--bg-input)",
  border: "1px solid var(--border-card)",
  borderRadius: 6,
  color: "var(--text-primary)",
  fontSize: "0.85rem",
  outline: "none",
  colorScheme: "light dark",
};

/** "2025-01-15" → "15/01/2025" */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// ─────────────────────────────────────────────────────────
// Modal de ELIMINACIÓN
// ─────────────────────────────────────────────────────────
export function DeleteInvoiceModal({ invoice, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [recibos, setRecibos] = useState([]);
  const [error, setError] = useState("");
  const [confirmRecibo, setConfirmRecibo] = useState(null);
  const [deletingRecibo, setDeletingRecibo] = useState(false);

  useEffect(() => {
    apiFetch(
      `/api/finance/factura/${invoice.id_factura ?? invoice.id}/recibos-aplicados/`,
    )
      .then((r) => r.json())
      .then((data) => {
        setRecibos(data.recibos ?? []);
        setChecking(false);
      })
      .catch(() => setChecking(false));
  }, [invoice]);

  async function handleDelete() {
    setLoading(true);
    setError("");
    const res = await apiFetch(
      `/api/finance/factura/${invoice.id_factura ?? invoice.id}/`,
      { method: "DELETE" },
    );
    if (res.status === 204) {
      onSuccess?.();
      onClose();
    } else if (res.status === 409) {
      const data = await res.json();
      setRecibos(data.recibos ?? []);
      setError(data.error ?? t("invoices.noPuedeEliminar"));
    } else {
      setError(t("invoices.errorEliminarFactura"));
    }
    setLoading(false);
  }

  async function handleDeleteRecibo(reciboId) {
    setDeletingRecibo(true);
    setError("");
    const res = await apiFetch(`/api/finance/recibo/${reciboId}/`, {
      method: "DELETE",
    });
    setDeletingRecibo(false);
    setConfirmRecibo(null);
    if (res.status === 204) {
      setRecibos((prev) => prev.filter((r) => r.recibo_id !== reciboId));
    } else {
      setError(t("invoices.errorEliminarRecibo"));
    }
  }

  const canDelete = !checking && recibos.length === 0;

  return (
    <>
      <div {...overlayClose(onClose)} style={OVERLAY} />
      <div style={MODAL_BASE}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                background: "rgba(248,113,113,0.15)",
                borderRadius: 8,
                padding: "8px 10px",
                color: "#f87171",
              }}
            >
              <i className="fa-solid fa-trash" style={{ fontSize: "1.1rem" }} />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  color: "var(--text-primary)",
                  fontSize: "1rem",
                  fontWeight: 700,
                }}
              >
                {t("invoices.eliminarFactura")}
              </h3>
              <p
                style={{
                  margin: "2px 0 0",
                  color: "var(--text-muted)",
                  fontSize: "0.78rem",
                }}
              >
                {invoice.numero_factura} - ₡{formatCurrency(invoice.monto)}
              </p>
            </div>
          </div>
          <CloseBtn onClick={onClose} />
        </div>
        <Divider />

        {checking ? (
          <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", padding: "8px 0" }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }} />
            {t("invoices.verificandoRecibos")}
          </p>
        ) : recibos.length > 0 ? (
          <>
            <WarningBox icon="fa-triangle-exclamation" color="#fb923c">
              {t("invoices.facturaConRecibos", { n: recibos.length })}
            </WarningBox>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "14px 0" }}>
              {recibos.map((r) => (
                <div
                  key={r.recibo_id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "var(--bg-input)",
                    border: "1px solid var(--border-card)",
                    borderRadius: 6,
                    padding: "8px 12px",
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: "0.85rem" }}>
                      {r.numero_recibo}
                    </span>
                    <span style={{ color: "var(--text-secondary)", fontSize: "0.8rem", marginLeft: 8 }}>
                      {t("invoices.abonoInfo", {
                        monto: formatCurrency(r.abono),
                        fecha: fmtDate(r.fecha_pago),
                      })}
                    </span>
                  </div>
                  {confirmRecibo === r.recibo_id ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ color: "#fca5a5", fontSize: "0.76rem" }}>
                        {t("common.confirmar")}?
                      </span>
                      <button
                        onClick={() => handleDeleteRecibo(r.recibo_id)}
                        disabled={deletingRecibo}
                        style={{
                          padding: "3px 10px",
                          background: "#ef4444",
                          color: "#fff",
                          border: "none",
                          borderRadius: 5,
                          fontSize: "0.76rem",
                          fontWeight: 600,
                          cursor: deletingRecibo ? "not-allowed" : "pointer",
                        }}
                      >
                        {deletingRecibo ? (
                          <i className="fa-solid fa-circle-notch fa-spin" />
                        ) : (
                          t("common.siEliminar")
                        )}
                      </button>
                      <button
                        onClick={() => setConfirmRecibo(null)}
                        style={{
                          padding: "3px 8px",
                          background: "transparent",
                          color: "var(--text-muted)",
                          border: "1px solid var(--border-card)",
                          borderRadius: 5,
                          fontSize: "0.76rem",
                          cursor: "pointer",
                        }}
                      >
                        {t("common.no")}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmRecibo(r.recibo_id)}
                      style={{
                        padding: "3px 10px",
                        background: "rgba(248,113,113,0.15)",
                        color: "#f87171",
                        border: "1px solid rgba(248,113,113,0.3)",
                        borderRadius: 5,
                        fontSize: "0.76rem",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      <i className="fa-solid fa-trash" style={{ marginRight: 4 }} />
                      {t("recibos.eliminarReciboBtn")}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {recibos.length === 0 && (
              <p style={{ color: "#4ade80", fontSize: "0.83rem" }}>
                {t("invoices.todosRecibosEliminados")}
              </p>
            )}
          </>
        ) : (
          <WarningBox icon="fa-circle-exclamation" color="#f87171">
            {t("invoices.confirmarEliminarFactura", { num: invoice.numero_factura })}
          </WarningBox>
        )}

        {error && <ErrorBox>{error}</ErrorBox>}

        <Divider />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <CancelBtn onClick={onClose} label={t("common.cancelar")} />
          <button
            onClick={handleDelete}
            disabled={!canDelete || loading}
            style={{
              padding: "8px 18px",
              background: !canDelete || loading ? "rgba(248,113,113,0.4)" : "#ef4444",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: !canDelete || loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> {t("common.eliminando")}
              </>
            ) : (
              <>
                <i className="fa-solid fa-trash" /> {t("invoices.eliminarFactura")}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Modal de EDICIÓN
// ─────────────────────────────────────────────────────────
export function EditInvoiceModal({ invoice, onClose, onSuccess }) {
  const { t } = useTranslation();
  const esCliente = useEsCliente();
  const [loading, setLoading] = useState(false);
  const [cuentasIngreso, setCuentasIngreso] = useState([]);
  const [warning, setWarning] = useState("");
  const [error, setError] = useState("");
  const [showRubrosModal, setShowRubrosModal] = useState(false);

  // `invoice.monto` es el TOTAL (base + IVA). El campo del formulario guarda el
  // valor DIGITADO: la base si modo=SUMAR, o el total si modo=INCLUIDO.
  const ivaIncluidoInicial = invoice.iva_incluido ?? Number(invoice.monto_iva) > 0;
  const modoInicial = invoice.iva_modo ?? "SUMAR";
  const totalInicial = Number(invoice.monto) || 0;
  const baseInicial = totalInicial - (Number(invoice.monto_iva) || 0);
  const montoInicial = modoInicial === "INCLUIDO" ? totalInicial : baseInicial;

  const [form, setForm] = useState({
    concepto: invoice.concepto ?? "",
    monto: montoInicial,
    fecha_emision: invoice.fecha_emision ?? "",
    fecha_vencimiento: invoice.fecha_vencimiento ?? "",
    cuenta_ingreso: invoice.cuenta_ingreso?.id ?? invoice.cuenta_ingreso ?? "",
    filial: invoice.filial?.id ?? invoice.filial ?? "",
    nombre_propietario: invoice.nombre_propietario ?? "",
    medida_anterior: invoice.medida_anterior ?? "",
    medida_actual: invoice.medida_actual ?? "",
    iva_incluido: ivaIncluidoInicial,
    tasa_iva: Number(invoice.tasa_iva) || 13,
    iva_modo: modoInicial,
    rubros: invoice.rubros ?? [],
  });

  const cuentaActual = cuentasIngreso.find((c) => c.id === Number(form.cuenta_ingreso));
  const esAgua = cuentaActual
    ? /agua/i.test(cuentaActual.nombre)
    : /agua/i.test(invoice.cuenta_ingreso?.nombre ?? "");

  const consumoEdit =
    esAgua && form.medida_anterior !== "" && form.medida_actual !== ""
      ? (Number(form.medida_actual) - Number(form.medida_anterior)).toFixed(2)
      : null;
  const consumoNeg = consumoEdit !== null && Number(consumoEdit) < 0;

  const [infoRecibos, setInfoRecibos] = useState(null);

  useEffect(() => {
    apiFetch("/api/finance/catalogo/detalle/?tipo=INGRESO&page_size=500")
      .then((r) => r.json())
      .then((d) => setCuentasIngreso(d.results ?? d))
      .catch(() => {});

    apiFetch(
      `/api/finance/factura/${invoice.id_factura ?? invoice.id}/recibos-aplicados/`,
    )
      .then((r) => { if (!r.ok) return null; return r.json(); })
      .then((d) => { if (d) setInfoRecibos(d); })
      .catch(() => {});
  }, [invoice]);

  function setField(k, v) {
    setForm((p) => {
      const next = { ...p, [k]: v };
      // Recalcular el aviso de saldo contra el TOTAL (base + IVA), no la base.
      if (infoRecibos && (k === "monto" || k === "tasa_iva" || k === "iva_incluido" || k === "iva_modo")) {
        const { total } = calcularIva(next.monto, next.tasa_iva, next.iva_incluido, next.iva_modo);
        previewWarning(total, Number(infoRecibos.total_abonado));
      }
      return next;
    });
  }

  function previewWarning(nuevoMonto, totalAbonado) {
    if (!infoRecibos || infoRecibos.recibos.length === 0) {
      setWarning("");
      return;
    }
    const saldo = nuevoMonto - totalAbonado;
    if (saldo < 0) {
      setWarning(
        t("invoices.saldoFavorWarning", {
          favor: formatCurrency(Math.abs(saldo)),
          abonado: formatCurrency(totalAbonado),
        }),
      );
    } else if (saldo > 0) {
      setWarning(
        t("invoices.saldoPendienteInfo", {
          pendiente: formatCurrency(saldo),
          abonado: formatCurrency(totalAbonado),
          total: formatCurrency(nuevoMonto),
        }),
      );
    } else {
      setWarning(t("invoices.montoExactoOk"));
    }
  }

  // ── Desglose multi-rubro (solo tenants Cliente) ──────────────────────
  function aplicarRubros(rubrosData, total) {
    setField("rubros", rubrosData);
    setField("monto", total);
    const primero = rubrosData[0];
    setField("cuenta_ingreso", primero.cuenta_ingreso);
    setShowRubrosModal(false);
  }

  function quitarRubros() {
    setField("rubros", []);
  }

  async function handleSave() {
    setLoading(true);
    setError("");
    const res = await apiFetch(
      `/api/finance/factura/${invoice.id_factura ?? invoice.id}/`,
      {
        method: "PATCH",
        body: JSON.stringify({
          concepto: form.concepto,
          monto: Number(form.monto), // valor digitado (base o total según modo)
          iva_incluido: form.iva_incluido,
          tasa_iva: form.iva_incluido ? Number(form.tasa_iva) : 0,
          iva_modo: form.iva_modo,
          fecha_emision: form.fecha_emision,
          fecha_vencimiento: form.fecha_vencimiento,
          cuenta_ingreso: Number(form.cuenta_ingreso),
          filial: Number(form.filial),
          nombre_propietario: form.nombre_propietario,
          medida_anterior:
            esAgua && form.medida_anterior !== "" ? Number(form.medida_anterior) : null,
          medida_actual:
            esAgua && form.medida_actual !== "" ? Number(form.medida_actual) : null,
          rubros: form.rubros.map((r) => ({
            cuenta_ingreso: cuentaIdDe(r),
            concepto: r.concepto || "",
            monto: Number(r.monto),
          })),
        }),
      },
    );
    const data = await res.json();
    if (res.ok) {
      onSuccess?.(data.warning ?? null);
      onClose();
    } else {
      setError(JSON.stringify(data));
    }
    setLoading(false);
  }

  const hasRecibos = infoRecibos && infoRecibos.recibos?.length > 0;

  return (
    <>
      <div {...overlayClose(onClose)} style={OVERLAY} />
      <div style={{ ...MODAL_BASE, width: "min(580px, 94vw)", maxHeight: "90vh", overflowY: "auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                background: "rgba(249,115,22,0.15)",
                borderRadius: 8,
                padding: "8px 10px",
                color: "#f97316",
              }}
            >
              <i className="fa-solid fa-pencil" style={{ fontSize: "1.1rem" }} />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  color: "var(--text-primary)",
                  fontSize: "1rem",
                  fontWeight: 700,
                }}
              >
                {t("invoices.editarFactura")}
              </h3>
              <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: "0.78rem" }}>
                {invoice.numero_factura}
              </p>
            </div>
          </div>
          <CloseBtn onClick={onClose} />
        </div>
        <Divider />

        {/* Info de recibos aplicados */}
        {hasRecibos && (
          <WarningBox icon="fa-circle-info" color="#fb923c">
            {t("invoices.facturaConRecibosInfo", {
              n: infoRecibos.recibos.length,
              total: formatCurrency(infoRecibos.total_abonado),
            })}
          </WarningBox>
        )}

        {/* Preview de advertencia al cambiar monto */}
        {warning && (
          <div
            style={{
              background: warning.startsWith("⚠️")
                ? "rgba(251,146,60,0.1)"
                : warning.startsWith("✅")
                  ? "rgba(74,222,128,0.1)"
                  : "rgba(96,165,250,0.1)",
              border: `1px solid ${
                warning.startsWith("⚠️")
                  ? "rgba(251,146,60,0.3)"
                  : warning.startsWith("✅")
                    ? "rgba(74,222,128,0.3)"
                    : "rgba(96,165,250,0.3)"
              }`,
              borderLeft: `3px solid ${
                warning.startsWith("⚠️") ? "#fb923c" : warning.startsWith("✅") ? "#4ade80" : "#60a5fa"
              }`,
              borderRadius: 6,
              padding: "8px 12px",
              color: warning.startsWith("⚠️")
                ? "#fdba74"
                : warning.startsWith("✅")
                  ? "#bbf7d0"
                  : "#bfdbfe",
              fontSize: "0.82rem",
              marginBottom: 12,
            }}
          >
            {warning}
          </div>
        )}

        {/* Formulario */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" }}>
          <div style={{ gridColumn: "span 2" }}>
            <label style={LABEL_STYLE}>{t("common.concepto")}</label>
            <input
              style={INPUT_STYLE}
              value={form.concepto}
              onChange={(e) => setField("concepto", e.target.value)}
            />
          </div>

          <div style={form.rubros.length > 0 ? { gridColumn: "span 2" } : {}}>
            <label style={LABEL_STYLE}>
              {form.iva_incluido && form.iva_modo === "INCLUIDO"
                ? "Monto (total, IVA incluido)"
                : "Monto (base, sin IVA)"}
            </label>
            <input
              type="number"
              style={{
                ...INPUT_STYLE,
                fontFamily: "monospace",
                ...(form.rubros.length > 0 ? { opacity: 0.7, cursor: "not-allowed" } : {}),
              }}
              value={form.monto}
              onChange={(e) => setField("monto", Number(e.target.value))}
              disabled={form.rubros.length > 0}
              min="0"
              step="1000"
            />
            {form.rubros.length > 0 && (
              <div style={{ fontSize: "0.73rem", color: "var(--text-muted)", marginTop: 3 }}>
                {t("rubros.montoCalculado", { n: form.rubros.length })}
              </div>
            )}
          </div>

          <div style={form.rubros.length > 0 ? { gridColumn: "span 2" } : {}}>
            <label style={LABEL_STYLE}>{t("invoices.cuentaIngreso")}</label>
            {form.rubros.length > 0 ? (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "var(--bg-input)", border: "1px solid var(--border-card)",
                borderRadius: 6, padding: "8px 10px", gap: 8, flexWrap: "wrap",
              }}>
                <span style={{ fontSize: "0.85rem", color: "var(--text-primary)", whiteSpace: "nowrap" }}>
                  <i className="fa-solid fa-list-ul" style={{ color: "#f97316", marginRight: 8 }} />
                  {form.rubros.length} {t("rubros.titulo").toLowerCase()}
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button type="button" onClick={() => setShowRubrosModal(true)}
                    style={{ padding: "4px 10px", background: "rgba(96,165,250,0.1)",
                      border: "1px solid rgba(96,165,250,0.3)", borderRadius: 6,
                      color: "#60a5fa", fontSize: "0.78rem", cursor: "pointer" }}>
                    {t("common.editar")}
                  </button>
                  <button type="button" onClick={quitarRubros}
                    style={{ padding: "4px 10px", background: "rgba(248,113,113,0.1)",
                      border: "1px solid rgba(248,113,113,0.3)", borderRadius: 6,
                      color: "#f87171", fontSize: "0.78rem", cursor: "pointer" }}>
                    {t("rubros.quitarRubros")}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <SearchSelect
                  icon="fa-tags"
                  options={cuentasIngreso.map((c) => ({ id: c.id, primary: c.codigo, secondary: c.nombre }))}
                  value={form.cuenta_ingreso ? Number(form.cuenta_ingreso) : null}
                  onChange={(id) => setField("cuenta_ingreso", id ? String(id) : "")}
                  placeholder={t("invoices.seleccionarCuenta")}
                />
                {esCliente && (
                  <button type="button" onClick={() => setShowRubrosModal(true)}
                    style={{ marginTop: 6, padding: "5px 10px", background: "transparent",
                      border: "1px dashed var(--border-card)", borderRadius: 6,
                      color: "var(--text-secondary)", fontSize: "0.76rem", cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <i className="fa-solid fa-list-ul" />
                    {t("rubros.botonMultiplesRubros")}
                  </button>
                )}
              </>
            )}
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <IvaSelector
              ivaIncluido={form.iva_incluido}
              tasaIva={form.tasa_iva}
              onIvaIncluidoChange={(v) => setField("iva_incluido", v)}
              onTasaChange={(v) => setField("tasa_iva", v)}
              base={Number(form.monto) || 0}
              moneda={invoice.moneda || "CRC"}
              modo={form.iva_modo || "SUMAR"}
              onModoChange={(v) => setField("iva_modo", v)}
            />
          </div>

          <div>
            <label style={LABEL_STYLE}>{t("invoices.fechaEmision")}</label>
            <input
              type="date"
              style={INPUT_STYLE}
              value={form.fecha_emision}
              onChange={(e) => setField("fecha_emision", e.target.value)}
            />
          </div>

          <div>
            <label style={LABEL_STYLE}>{t("invoices.fechaVencimiento")}</label>
            <input
              type="date"
              style={INPUT_STYLE}
              value={form.fecha_vencimiento}
              onChange={(e) => setField("fecha_vencimiento", e.target.value)}
            />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={LABEL_STYLE}>{t("invoices.propietario")}</label>
            <input
              style={INPUT_STYLE}
              value={form.nombre_propietario}
              onChange={(e) => setField("nombre_propietario", e.target.value)}
            />
          </div>
        </div>

        {/* ── Medidas de agua (condicional) ───────────────────────── */}
        {esAgua && (
          <div
            style={{
              background: "rgba(21,101,192,0.08)",
              border: "1px solid rgba(21,101,192,0.25)",
              borderLeft: "3px solid #1565C0",
              borderRadius: 6,
              padding: "12px 14px",
              marginTop: 4,
            }}
          >
            <div style={{ color: "#90CAF9", fontWeight: 600, fontSize: "0.82rem", marginBottom: 10 }}>
              <i className="fa-solid fa-droplet" style={{ marginRight: 6 }} />
              {t("invoices.lecturasAgua")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
              <div>
                <label style={LABEL_STYLE}>
                  {t("invoices.medidaAnterior")} <span style={{ color: "#f87171" }}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  style={INPUT_STYLE}
                  placeholder={t("invoices.ejMedida1")}
                  value={form.medida_anterior}
                  onChange={(e) => setField("medida_anterior", e.target.value)}
                />
              </div>
              <div>
                <label style={LABEL_STYLE}>
                  {t("invoices.medidaActual")} <span style={{ color: "#f87171" }}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  style={{
                    ...INPUT_STYLE,
                    ...(consumoNeg
                      ? { borderColor: "#f87171", boxShadow: "0 0 0 2px rgba(248,113,113,0.2)" }
                      : {}),
                  }}
                  placeholder={t("invoices.ejMedida2")}
                  value={form.medida_actual}
                  onChange={(e) => setField("medida_actual", e.target.value)}
                />
              </div>
            </div>
            {consumoEdit !== null && (
              <div
                style={{
                  marginTop: 8,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: consumoNeg ? "#f87171" : "#81C784",
                }}
              >
                {consumoNeg
                  ? t("invoices.medidaError")
                  : `💧 ${t("invoices.consumo")}: ${consumoEdit} m³`}
              </div>
            )}
          </div>
        )}

        {error && <ErrorBox>{error}</ErrorBox>}

        <Divider />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <CancelBtn onClick={onClose} label={t("common.cancelar")} />
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              padding: "8px 18px",
              background: loading ? "rgba(249,115,22,0.5)" : "#f97316",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> {t("cxp.guardando")}
              </>
            ) : (
              <>
                <i className="fa-solid fa-save" /> {t("recibos.guardarCambios")}
              </>
            )}
          </button>
        </div>
      </div>

      <RubrosModal
        open={showRubrosModal}
        onClose={() => setShowRubrosModal(false)}
        tipo="INGRESO"
        initialRubros={form.rubros}
        onConfirm={aplicarRubros}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Helpers de UI
// ─────────────────────────────────────────────────────────
function Divider() {
  return <div style={{ borderTop: "1px solid var(--border-card)", margin: "16px 0" }} />;
}
function CloseBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        color: "var(--text-muted)",
        cursor: "pointer",
        fontSize: "1.15rem",
        lineHeight: 1,
        padding: "2px 4px",
      }}
    >
      ✕
    </button>
  );
}
function CancelBtn({ onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "8px 16px",
        background: "transparent",
        border: "1px solid var(--border-card)",
        borderRadius: 7,
        color: "var(--text-muted)",
        fontSize: "0.84rem",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}
function WarningBox({ children, icon, color }) {
  return (
    <div
      style={{
        background: `${color}18`,
        border: `1px solid ${color}44`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 6,
        padding: "9px 12px",
        color,
        fontSize: "0.83rem",
        marginBottom: 12,
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
      }}
    >
      <i className={`fa-solid ${icon}`} style={{ marginTop: 2, flexShrink: 0 }} />
      <span>{children}</span>
    </div>
  );
}
function ErrorBox({ children }) {
  return (
    <div
      style={{
        background: "rgba(248,113,113,0.1)",
        border: "1px solid rgba(248,113,113,0.3)",
        borderLeft: "3px solid #f87171",
        borderRadius: 6,
        padding: "8px 12px",
        color: "#fca5a5",
        fontSize: "0.82rem",
        marginTop: 10,
      }}
    >
      <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 5 }} />
      {children}
    </div>
  );
}
